export type StoreScope = 'user' | 'device';

export interface Store<T> {
  read: (userId?: string | null) => T | null;
  write: (value: T, userId?: string | null) => void;
  remove: (userId?: string | null) => void;
  /**
   * Is there anything under this store's key? (#2183)
   *
   * ABSENT vs STORED NULL — the one thing `read` cannot express. With no
   * `sanitize` configured, `sanitize()` below returns the parsed value as-is,
   * so `read` answers `null` for a key nobody ever wrote and for one holding a
   * stored `null` alike. A caller that must tell those apart asks the key
   * rather than the value.
   *
   * SCOPE IS THE EFFECTIVE KEY ONLY, while `read` also falls back to the
   * pre-envelope `cfg.key`: a `MIGRATABLE_DEVICE_KEYS` entry whose only value
   * is the legacy one reads back through `read` while `has` says false (C2).
   * No key reaching the kit's seam can be migratable: the seam's only
   * `createStore` call site (`src/lib/resourceKitServices.tsx`) prefixes every
   * key with `hgs.`, and the migratable set is `theme` / `locale`. #2183 puts
   * that migration path out of scope, so this is recorded here rather than closed.
   *
   * Asking never mutates storage: no parse, no sanitize, no discard, unlike
   * `read`, which removes what it rejects before answering `null`.
   */
  has: (userId?: string | null) => boolean;
}

/**
 * Exported because it IS `createStore`'s public parameter: the package emits
 * declarations, and a config type only the implementation can name makes the
 * emitted `.d.ts` unreadable to its own consumers.
 */
export interface StoreConfig<T> {
  key: string;
  version: number;
  scope?: StoreScope;
  sanitize?: (raw: unknown) => T | null;
}

interface StoredEnvelope {
  version: number;
  value: unknown;
}

const MIGRATABLE_DEVICE_KEYS = new Set(['theme', 'locale']);

function isEnvelope(raw: unknown): raw is StoredEnvelope {
  return (
    typeof raw === 'object' &&
    raw !== null &&
    typeof (raw as Record<string, unknown>).version === 'number' &&
    Object.prototype.hasOwnProperty.call(raw, 'value')
  );
}

function parseLegacyValue(serialized: string): unknown {
  try {
    return JSON.parse(serialized) as unknown;
  } catch (error) {
    if (error instanceof SyntaxError) return serialized;
    throw error;
  }
}

export function createStore<T>(cfg: StoreConfig<T>): Store<T> {
  const scope = cfg.scope ?? 'user';

  const effectiveKey = (userId?: string | null): string =>
    scope === 'user' && userId ? `${userId}:${cfg.key}` : `device:${cfg.key}`;

  const reportDiscard = (): void => {
    console.error(cfg.key, cfg.version);
  };

  const removeKey = (key: string, reportFailure: boolean): boolean => {
    try {
      window.localStorage.removeItem(key);
      return true;
    } catch {
      if (reportFailure) reportDiscard();
      return false;
    }
  };

  // A removal that throws is swallowed here — `reportDiscard` below reports
  // this rejection either way — so the entry survives, `has` still answers
  // true, and a `remove` that throws AGAIN is the retry that does get reported
  // ("keeps the entry, and reports twice…" in `src/lib/__tests__/store.test.ts`).
  const discard = (key: string): null => {
    removeKey(key, false);
    reportDiscard();
    return null;
  };

  const sanitize = (raw: unknown, key: string): T | null => {
    if (!cfg.sanitize) return raw as T;
    try {
      return cfg.sanitize(raw) ?? discard(key);
    } catch {
      return discard(key);
    }
  };

  const writeKey = (key: string, value: T): boolean => {
    try {
      window.localStorage.setItem(
        key,
        JSON.stringify({ version: cfg.version, value } satisfies StoredEnvelope),
      );
      return true;
    } catch {
      reportDiscard();
      return false;
    }
  };

  const readCurrent = (key: string, serialized: string): T | null => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(serialized) as unknown;
    } catch {
      return discard(key);
    }
    if (!isEnvelope(parsed) || parsed.version !== cfg.version) return discard(key);
    return sanitize(parsed.value, key);
  };

  const has = (userId?: string | null): boolean => {
    try {
      return window.localStorage.getItem(effectiveKey(userId)) !== null;
    } catch {
      // Storage disabled (private mode, blocked site data) throws on ACCESS,
      // and `read` answers `null` in exactly that case. Answering "absent"
      // keeps the pair consistent: with no storage there is no entry to call
      // corrupt, and a caller that gated a discard on `has` must not issue the
      // discard here. Every caller reads first, and `read`'s own catch is
      // where this outage gets reported.
      return false;
    }
  };

  const read = (userId?: string | null): T | null => {
    const key = effectiveKey(userId);
    try {
      const current = window.localStorage.getItem(key);
      if (current !== null) return readCurrent(key, current);

      const legacy = window.localStorage.getItem(cfg.key);
      if (legacy === null) return null;
      if (!MIGRATABLE_DEVICE_KEYS.has(cfg.key)) {
        removeKey(cfg.key, true);
        return null;
      }

      const migrated = sanitize(parseLegacyValue(legacy), cfg.key);
      if (migrated !== null) writeKey(key, migrated);
      removeKey(cfg.key, true);
      return migrated;
    } catch {
      reportDiscard();
      return null;
    }
  };

  return {
    read,
    write: (value, userId) => {
      writeKey(effectiveKey(userId), value);
    },
    remove: (userId) => {
      removeKey(effectiveKey(userId), true);
    },
    has,
  };
}
